import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Image from './image.jsx';
import Lightbox, {ImageSlide} from 'yet-another-react-lightbox';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Slideshow from 'yet-another-react-lightbox/plugins/slideshow';
import { QRCode } from "react-qr-code";
import toast from "react-hot-toast";
import {usePage} from '@inertiajs/react';
import {Zoom} from 'yet-another-react-lightbox/plugins';
import Video from './video';
import {useTranslation} from 'react-i18next';
import VideoSlide from './videoSlide.jsx';

function isDesktop() {
    // Prefer Client Hints (most reliable)
    if (navigator.userAgentData && typeof navigator.userAgentData.mobile === 'boolean') {
        return !navigator.userAgentData.mobile;
    }
    // Fallback heuristics
    const ua = navigator.userAgent || '';
    const platform = navigator.platform || '';
    const isIPadOS = platform === 'MacIntel' && navigator.maxTouchPoints > 1; // iPadOS masquerades as Mac
    const isMobileUA = /Android|iPhone|iPod|iPad|Mobile/i.test(ua);
    return !isMobileUA && !isIPadOS;
}

function Album({photos, aspectRatio, event, reloadUrl, slideshowAccess = false}) {
    const {auth} = usePage().props
    const [pictures, setPictures] = useState(photos);
    const picturesRef = useRef(photos);
    const [index, setIndex] = useState(-1);
    const indexRef = useRef(-1);
    const [duration, setDuration] = useState(5000);
    const [showQR, setShowQR] = useState(false);
    const timeout = useRef(null);
    const slideshowRef = useRef(null);
    const hideRef = useRef(null);
    const {t} = useTranslation();

    useEffect(() => {
        setPictures(photos); // reload hook when switching disposable
    }, [photos]);
    const images = useMemo(() => {
        return pictures.map(p => {

            if (p.file_type === 'video') {
                return {
                    ...p,
                    type: 'video',
                    playHls: p.playHls,
                    description: p.timestamp,
                    descriptionTextAlign: 'center',
                    canDelete: !!p.user && p.user.name === auth?.user?.name,
                    title: p.user.name + ' @ ' + event.name,
                }
            }
            return {
                type: 'photo',
                src: p.url,
                previewUrl: p.previewUrl,
                shareUrl: p.shareUrl,
                gallery_cache_key: p.gallery_cache_key,
                title: p.user.name + ' @ ' + event.name,
                description: p.timestamp,
                descriptionTextAlign: 'center',
                blurhash: p.blurhash,
                id: p.id,
                canDelete: !!p.user && p.user.name === auth?.user?.name,
            }
        })
    }, [pictures.length, event, auth?.user]);

    const slideshowStart = useCallback(async () => {
        const currentIndex = indexRef.current;
        let data;
        try {
            data = (await axios.get(reloadUrl)).data;
        } catch (e) {
            console.warn(e);
            timeout.current = setTimeout(slideshowStart, 10000);
            return;
        }

        const currentPictures = Object.assign([], picturesRef.current);

        const newSinceRender = currentPictures.lastIndexOf(p => p.newSinceRender);

        let minIndex = newSinceRender < currentIndex ? currentIndex + 2 : newSinceRender;

        const allNewPictures = data
            .data
            .filter(p => currentPictures.findIndex(p2 => p2.id === p.id) === -1)
            .map(p => {
                p.newSinceRender = true
                return p
            });
        console.log(allNewPictures.length, 'new');

        currentPictures.splice(minIndex, 0, ...allNewPictures)

        const newPics = currentPictures.map((p, i) => {
            if (p.newSinceRender && currentIndex > i) {
                p.newSinceRender = false;
            }
            return p
        })

        let schedule = true;
        if (newPics.length > 0 && slideshowRef.current && !slideshowRef.current.playing) {
            console.log('start playing');
            setTimeout(() => slideshowRef.current?.play(), 500);
            // play() triggers a reschedule event that triggers this function.
            schedule = false;
        }

        setPictures(newPics);
        picturesRef.current = newPics;
        if (schedule) {
            timeout.current = setTimeout(slideshowStart, 30000);
        }
    }, []);

    const onListener = useCallback((e) => {
        if (hideRef.current){
            clearTimeout(hideRef.current);
        }
        showToolbar();
        hideRef.current = setTimeout(() => {
            if (slideshowRef.current?.playing) {
                console.log('not moved for 2 seconds.');
                document.querySelector('.yarl__toolbar')?.classList?.add('hidden-toolbar');
                document.querySelector('.yarl__navigation_next')?.classList?.add('md:hidden');
                document.querySelector('.yarl__navigation_prev')?.classList?.add('md:hidden');
            }
        }, 2000)
    }, []);

    useEffect(() => {
        document.addEventListener("mousemove", onListener);

        return () => {
            document.removeEventListener('mousedown', onListener)
        }
    }, [indexRef.current !== -1]);

    const showToolbar = useCallback(() => {
        document.querySelector('.yarl__toolbar')?.classList?.remove('hidden-toolbar');
        document.querySelector('.yarl__navigation_next')?.classList?.remove('md:hidden');
        document.querySelector('.yarl__navigation_prev')?.classList?.remove('md:hidden');
    }, []);


    const slideShowStop = useCallback(() => {
        console.log('stop slideshow');
        if (timeout.current) {
            clearTimeout(timeout.current);
        }
    }, []);

    const saveSlide = useCallback(async (slide) => {
        const isVideo = slide.type === 'video';
        const fileName = (slide.gallery_cache_key ?? 'photo') + (isVideo ? '.mp4' : '.jpg');

        const downloadBlob = (blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        };

        // Last-resort fallback: navigate to URL so user can long-press to save.
        // Use location.href instead of window.open — in-app webviews (Instagram, WhatsApp) block popups.
        const openUrl = () => {
            if (!slide.shareUrl) {
                toast.error(t('Could not save. Please try again later.'));
                return;
            }
            location.href = slide.shareUrl;
            toast(t('Long-press the image to save it.'), {duration: 5000});
        };

        const toastId = toast.loading(t('Preparing...'));
        let blob;
        try {
            const res = await fetch(slide.shareUrl, {cache: 'no-cache'});
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }
            blob = await res.blob();
        } catch {
            toast.dismiss(toastId);
            openUrl();
            return;
        }
        toast.dismiss(toastId);

        if (isDesktop()) {
            downloadBlob(blob);
            return;
        }

        // Mobile: prefer Web Share API with file (saves to camera roll on iOS/Android)
        const file = new File([blob], fileName, {type: isVideo ? 'video/mp4' : 'image/jpeg'});
        const shareData = {files: [file]};
        if (navigator.share && navigator.canShare?.(shareData)) {
            try {
                await navigator.share(shareData);
                return;
            } catch (e) {
                // AbortError = user dismissed the share sheet — not an error
                if (e.name === 'AbortError') {
                    return;
                }
            }
        }

        // Fallback: blob anchor download works on Android Chrome even when share API is unavailable.
        // On iOS Safari, <a download> is ignored for blobs, so openUrl() is the final fallback.
        try {
            downloadBlob(blob);
        } catch {
            openUrl();
        }
    }, [t]);

    const deleteSlide = useCallback(async (slide) => {
        if (!confirm(t('are you sure you want to delete this {{captureType}}?', {captureType: slide.type}))) {
            return;
        }
        try {
            await axios.delete(`/camera/${event.id}/${slide.type}s/${slide.id}`);
            setPictures(pictures.filter(p => p.id !== slide.id))
            toast.success(t('{{captureType}} deleted', {captureType: slide.type}));
        } catch (e) {
            toast.error(t('Could not delete this {{captureType}}.', {captureType: slide.type}))
        } finally {
            setIndex(-1);

        }
    }, [event, pictures]);

    const plugins = useMemo(() => {
        const plugins = [Fullscreen, Zoom];
        if (slideshowAccess) {
            plugins.push(Slideshow);
        }
        return plugins;
    }, [slideshowAccess])

    return (
        <div className="flex flex-col grow h-full">
            {images.length > 0 &&
                <div className="grid gap-4 grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8">
                    {images.map((p, i) => {
                        if (p.type === 'video' && !p.previewUrl) {
                            return <Video
                                aspectRatio={aspectRatio}
                                key={p.id}
                                video={p}
                                onClick={() => {
                                    setIndex(i);
                                }} />
                        }

                        return (
                            <Image aspectRatio={aspectRatio}
                                   recordDuration={p.type === 'video' ? p.duration_formatted : null}
                                   key={p.id}
                                   url={p.previewUrl}
                                   blurhash={p.blurhash}
                                   onClick={() => {
                                       setIndex(i);
                                   }} />)

                    })}
                </div>}
            {images.length === 0 && slideshowAccess && (
                <div className="flex items-center justify-center flex-col">
                    <div
                        className="text-white-foreground text-2xl text-center">{t('No captures have been uploaded yet.')}
                        {t('Photos will appear automatically when they are uploaded.')}
                    </div>
                    <button
                        className="flex-wrap shadow-sm transition duration-300 transform px-6 py-3 font-medium text-white-foreground mt-[10px] primary-camera-button/80 focus:bg-primary-500/80 text-center flex flex-row items-center justify-center"
                        onClick={() => {
                            setIndex(0);
                            slideshowStart();
                        }}>
                        {t('Click here to start the live slideshow')}
                    </button>
                </div>
            )}

            <Lightbox
                toolbar={{
                    buttons: [
                        event.join_url && slideshowAccess ? <div className="hidden md:flex items-center justify-center">
                            <input id="enableQR" type="checkbox" className="drop-shadow-2xl"
                                   onChange={event => setShowQR(!showQR)}
                                   checked={showQR}
                            />
                            <label className='text-white-foreground ml-2 drop-shadow-xl' htmlFor="enableQR">
                                {t('Show QR')}
                            </label>
                        </div> : null,
                        'fullscreen',
                        slideshowAccess ? <select onChange={event => setDuration(event.target.value)}
                                                  value={duration}
                                                  className="bg-transparent text-white-foreground border-none focus:ring-0 caret-white"
                        >
                            <option className="bg-black" value="3000">3s</option>
                            <option className="bg-black" value="5000">5s</option>
                            <option className="bg-black" value="10000">10s</option>
                            <option className="bg-black" value="30000">30s</option>
                            <option className="bg-black" value="60000">60s</option>
                        </select> : null,
                        slideshowAccess ? 'slideshow' : null,
                        "close",
                        slideshowAccess && event.join_url ? <div
                            className={`py-1 hidden md:flex flex-col tool-qr-code w-64 fixed bottom-5 left-5 transition ${showQR ? 'opacity-100 z-99999999999' : 'opacity-0'}`

                            }>
                            <div
                                className="py-2 rounded-sm mb-2 bg-opacity-25 text-white-foreground bg-black backdrop-blur-sm text-center text-2xl">
                                <span>{t('Scan to add photos')}</span>
                            </div>
                            <div className="bg-white flex p-1 border-2 rounded-lg border-purple-500">
                                <QRCode value={event.join_url} />
                            </div>
                        </div> : null
                    ],
                }}
                slideshow={{
                    delay: duration,
                    ref: slideshowRef,
                }}
                render={{
                    slide: ({slide, offset}) =>
                        slide.type === "video" ? (
                            <VideoSlide isPlaying={offset === 0} slide={slide} />
                        ) : <ImageSlide slide={slide} />,
                    slideFooter: ({slide}) => {
                        return (
                            <>
                                <button onClick={() => saveSlide(slide)}
                                        className={
                                            `md:hidden focus:border-purple-500 flex justify-center items-center text-white-foreground absolute ${slide.canDelete ? 'right-[20px]' : ''} bottom-[20px] bg-black/40 rounded-full px-4 py-2`
                                        }>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                         strokeWidth="1.5"
                                         stroke="currentColor" className="size-6 mr-2">
                                        <path strokeLinecap="round" strokeLinejoin="round"
                                              d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                                    </svg>
                                    {t('Save {{captureType}}', {captureType: slide.type})}
                                </button>
                                {slide.canDelete && <button onClick={() => deleteSlide(slide)}
                                                            className="md:hidden focus:border-purple-500 flex justify-center left-[20px] items-center text-red-500 absolute bottom-[20px] bg-black/40 rounded-full px-4 py-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                         strokeWidth="1.5" stroke="currentColor" className="size-6">
                                        <path strokeLinecap="round" strokeLinejoin="round"
                                              d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                                    </svg>
                                    {t('Delete {{captureType}}', {captureType: slide.type})}
                                </button>}
                            </>
                        )
                    }
                }}

                on={{
                    slideshowStart: slideshowStart,
                    slideshowStop: slideShowStop,
                    exiting: () => {
                        slideShowStop()
                        setShowQR(false);
                        if (hideRef.current) {
                            clearTimeout(hideRef.current)
                        }
                        showToolbar();
                    },
                    view: ({index: currentIndex}) => {
                        setIndex(currentIndex);
                        indexRef.current = currentIndex;
                    },
                    entering: () => setShowQR(true)
                }}
                carousel={{
                    imageFit: 'contain'
                }}
                slides={images}
                open={index >= 0}
                index={index}
                draggable={false}
                close={() => setIndex(-1)}
                // enable optional lightbox plugins
                plugins={plugins}
            />
            {/* carousel zindex is 9999 */}
        </div>
    );
}

export default Album;
